import type { V2ComponentType } from "../types";

type ChangeHandler = (value: unknown) => void;

export interface ComponentAdapter {
  getValue: (raw: unknown) => unknown;
  setValue: (onChange: ChangeHandler, next: unknown) => void;
  format: (value: unknown) => unknown;
}

const textAdapter: ComponentAdapter = {
  getValue: raw => String(raw ?? ""),
  setValue: (onChange, next) => onChange(String(next ?? "")),
  format: value => String(value ?? ""),
};

const booleanAdapter: ComponentAdapter = {
  getValue: raw => raw === true || raw === "true",
  setValue: (onChange, next) => onChange(Boolean(next)),
  format: value => Boolean(value),
};

const numberAdapter: ComponentAdapter = {
  getValue: raw => String(raw ?? ""),
  setValue: (onChange, next) => {
    const asString = String(next ?? "").trim();
    if (asString.length === 0) {
      onChange(null);
      return;
    }

    if (asString === "-" || asString === "." || asString === "-.") {
      onChange(asString);
      return;
    }

    const asNumber = Number(asString);
    if (Number.isNaN(asNumber)) {
      onChange(null);
    } else {
      onChange(asString);
    }
  },
  format: value => {
    if (value === "" || value == null) {
      return null;
    }
    const asString = String(value);

    // Pass intermediate/formatting characters as strings
    if (
      asString === "-" ||
      asString === "." ||
      asString === "-." ||
      asString.endsWith(".") ||
      (asString.includes(".") && asString.endsWith("0"))
    ) {
      return asString;
    }

    // Only parse normal numbers to float
    const asNumber = Number(value);
    return Number.isNaN(asNumber) ? null : asNumber;
  },
};

const defaultAdapter: ComponentAdapter = {
  getValue: raw => raw,
  setValue: (onChange, next) => onChange(next),
  format: value => value,
};

const registry: Partial<Record<V2ComponentType, ComponentAdapter>> = {
  text: textAdapter,
  textArea: textAdapter,
  date: textAdapter,
  time: textAdapter,
  datetime: textAdapter,
  gps: textAdapter,
  signature: textAdapter,
  yachtinfo: textAdapter,
  yachtData: textAdapter,
  users: textAdapter,
  list: textAdapter,
  label: textAdapter,
  check: booleanAdapter,
  number: numberAdapter,
  loclookup: defaultAdapter,
};

export function getComponentAdapter(type: V2ComponentType): ComponentAdapter {
  return registry[type] ?? defaultAdapter;
}
